<template>
  <div>
    <v-row>
      <v-col cols="12">
        <v-autocomplete
          id="get_pub_key_version"
          :label="$tc('type')"
          :items="typesOptions"
          item-text="name"
          item-value="type"
          v-model="typeOption"
        />
      </v-col>

      <v-col
        v-if="hasCompressedOption"
        cols="12"
      >
        <v-switch
          id="KeysNewModal_compressed"
          color="green"
          :label="$tc('compressed')"
          v-model="compressed"
        />
      </v-col>
    </v-row>

    <v-row no-gutters justify="end" class="mb-8">
      <v-btn
        color="blue"
        :disabled="$v.$invalid"
        :dark="!$v.$invalid"
        @click="submit"
        id="get_pub_key_submit"
      >
        {{ $t("get_pbk_btn") }}
      </v-btn>
    </v-row>

    <v-row no-gutters>
      <v-col cols="11">
        <v-textarea
          id="blockchain_export_outBlock"
          class="monospace"
          auto-grow
          row="6"
          row-height="25"
          label="Out Block"
          readonly
          v-model="outBlock"
          persistent-hint
          :hint="formatHint"
        />
      </v-col>
      <v-col cols="1">
        <v-row no-gutters>
          <v-col cols="12">
            <template v-if="typeOption === 'bip32Xpub'">
              <v-tooltip left>
                <template #activator="{ on }">
                  <v-btn
                    id="blockchain_export_outBlock_format"
                    icon
                    v-on="on"
                    class="my-1"
                    color="contrasty"
                    @click="changeMaterialFormat"
                  >
                    <v-icon>check_circle_outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ formatTooltip }}</span>
              </v-tooltip>
            </template>
          </v-col>
          <v-col cols="12">
            <!-- CLIPBOARD -->
            <v-tooltip left>
              <template #activator="{ on }">
                <v-btn
                  id="blockchain_outBlock_clipboard"
                  icon
                  v-on="on"
                  class="my-1"
                  color="contrasty"
                  v-clipboard:copy="outBlock"
                >
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc("copy") }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12">
            <!-- SAVE -->
            <v-tooltip left>
              <template #activator="{ on }">
                <v-btn
                  id="blockchain_outBlock_saveFile"
                  icon
                  v-on="on"
                  class="my-1"
                  color="contrasty"
                  @click="saveToFile(outBlock)"
                >
                  <v-icon>save</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc("save_to_file") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'

const BIP32_XPRIV = 'BIP32 XPRIV'
const BIP32_EDWARDS_XPRIV = 'BIP32 Edwards XPRIV'
const EDWARDS = 'Edwards'
const SECP = 'SECP'

export default {
  props: {
    name: {
      type: String,
      required: true,
      default: null
    },
    keyInfo: {
      type: Object,
      required: true,
      default: null
    }
  },
  data () {
    return {
      outBlock: null,
      type: null,
      typeOption: null,
      compressed: false,
      formatHex: true
    }
  },
  computed: {
    keyType () {
      return this.$store.getters['keys/getKeyType']({ type: this.keyInfo.type }).type
    },
    hasCompressedOption () {
      return this.typeOption === 'sec1'
    },
    typesOptions () {
      const typesOptions = [
        {
          name: this.$tc('sec1'),
          type: 'sec1'
        },
        {
          name: this.$tc('bip340'),
          type: 'bip340'
        },
        {
          name: this.$tc('ed25519'),
          type: 'ed25519'
        },
        {
          name: this.$tc('algorand'),
          type: 'algorand'
        },
        {
          name: this.$tc('bip32Xpub'),
          type: 'bip32Xpub'
        },
        {
          name: this.$tc('sol'),
          type: 'sol'
        }
      ]

      const typesOptionsBip32Xpriv = [
        {
          name: this.$tc('sec1'),
          type: 'sec1'
        },
        {
          name: this.$tc('bip340'),
          type: 'bip340'
        },
        {
          name: this.$tc('bip32Xpub'),
          type: 'bip32Xpub'
        }
      ]

      const typesOptionsEd25519 = [
        {
          name: this.$tc('ed25519'),
          type: 'ed25519'
        },
        {
          name: this.$tc('algorand'),
          type: 'algorand'
        },
        {
          name: this.$tc('bip32Xpub'),
          type: 'bip32Xpub'
        },
        {
          name: this.$tc('sol'),
          type: 'sol'
        }
      ]

      const typesOptionsSecp = [
        {
          name: this.$tc('sec1'),
          type: 'sec1'
        },
        {
          name: this.$tc('bip340'),
          type: 'bip340'
        }
      ]

      const typesOptionsBip32Ed25519Xpriv = [
        {
          name: this.$tc('ed25519'),
          type: 'ed25519'
        },
        {
          name: this.$tc('algorand'),
          type: 'algorand'
        },
        {
          name: this.$tc('bip32Xpub'),
          type: 'bip32Xpub'
        },
        {
          name: this.$tc('sol'),
          type: 'sol'
        }
      ]

      switch (this.keyType) {
        case BIP32_XPRIV:
          return typesOptionsBip32Xpriv

        case EDWARDS:
          return typesOptionsEd25519

        case SECP:
          return typesOptionsSecp

        case BIP32_EDWARDS_XPRIV:
          return typesOptionsBip32Ed25519Xpriv

        default:
          return typesOptions
      }
    },
    formatTooltip () {
      if (this.formatHex) {
        return this.$tc('format_string')
      } else {
        return this.$tc('format_hex')
      }
    },
    formatHint () {
      if (this.typeOption === 'bip32Xpub') {
        if (this.formatHex) {
          return this.$tc('format_hex_hint')
        } else {
          return this.$tc('format_string_hint')
        }
      } else {
        return ''
      }
    }
  },
  validations () {
    const validation = {}

    validation.typeOption = {
      required
    }

    return validation
  },
  methods: {
    changeMaterialFormat () {
      const material = this.outBlock

      if (!this.formatHex) {
        let hex = ''
        for (let i = 0; i < material.length; i++) {
          hex += '' + material.charCodeAt(i).toString(16)
        }
        this.outBlock = hex
        this.formatHex = true
      } else {
        let string = ''
        for (let i = 0; i < material.length; i += 2) {
          string += String.fromCharCode(parseInt(material.substr(i, 2), 16))
        }

        this.outBlock = string
        this.formatHex = false
      }
    },
    saveToFile (res) {
      if (res) {
        const FileSaver = require('file-saver')
        const blob = new Blob([res], { type: 'text/plain;charset=utf-8' })

        const formatName = this.typesOptions.find(item => item.type === this.type).name.replace(/\s+/g, '')

        const fileName = this.name + formatName + '.bin'
        FileSaver.saveAs(blob, fileName)
      }
    },
    submit () {
      const typeValuesList = [
        {
          name: 'sec1',
          type: 1
        },
        {
          name: 'bip340',
          type: 3
        },
        {
          name: 'ed25519',
          type: 4
        },
        {
          name: 'algorand',
          type: 5
        },
        {
          name: 'bip32Xpub',
          type: 6
        },
        {
          name: 'sol',
          type: 7
        }
      ]

      const sec1Compressed = 2

      if (this.compressed === true) {
        this.type = sec1Compressed
      } else {
        const typeOptionSelected = typeValuesList.find(item => {
          if (item.name === this.typeOption) {
            return item
          } else {
            return null
          }
        })

        this.type = typeOptionSelected.type
      }

      this.$store.dispatch('hsm/bchain_get_pbk', {
        body: {
          key: this.name,
          type: this.type
        }
      })
        .then((res) => {
          this.outBlock = res.pbk
          this.$notify.success({})
        })
        .catch((err) => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
    }
  },
  watch: {
    typeOption (val) {
      this.outBlock = null
    }
  }
}
</script>
